<template>
  <div
    :class="[
      'asc-ag-grid-filter--container',
      'asc-ag-grid-filter--container-accent',
      isGridFiltersHidden ? 'asc-ag-grid-filter--no-filters-height' : null,
    ]"
  >
    <asc-ag-grid
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :group-default-expanded="groupDefaultExpanded"
      :row-data="rowData"
      :grid-size="gridSize"
      :get-row-id="getRowId ? getRowId : undefined"
      :get-row-height="getRowHeight"
      v-on="{
        onSelectionChanged: attachRowSelectedHandler
          ? handleChangeInSelection
          : () => {},
        onRowSelected: attachRowSelectedHandler ? handleRowSelected : () => {},
      }"
    ></asc-ag-grid>
    <template v-if="hasData">
      <asc-grid-pagination
        :show-rows-per-page="showRowsPerPage"
        :page-sizes-override="pageSizes"
        :selected-page-size-override="pageSizeSelected"
        :grid-api="gridApi"
      ></asc-grid-pagination>
    </template>
  </div>
</template>

<script>
import { AscAgGridPropsMixin } from '@/components/mixins'
import AscAgGrid from './AscAgGrid'
import AscGridPagination from '@/components/molecules/AscGridPagination'
import _ from 'lodash'

export default {
  name: 'AscAgGridFilter',

  components: {
    AscAgGrid,
    AscGridPagination,
  },

  mixins: [AscAgGridPropsMixin],

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    pageSizeSelected: {
      type: Number,
      default: 25,
    },
    pageSizes: {
      type: Array,
      default: () => [],
    },
    selections: {
      type: Array,
      default: () => [],
    },
    getRowId: {
      type: Function,
      default: undefined,
    },
    showRowsPerPage: {
      type: Boolean,
      default: false,
    },
    groupDefaultExpanded: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      pageSize: this.gridOptions.paginationPageSize
        ? this.gridOptions.paginationPageSize
        : 25,
      gridApi: null,
      allRowsProgramaticallySelected: false,
    }
  },

  computed: {
    hasData() {
      return this.gridOptions.pagination && this.rowData.length > this.pageSize
    },

    isGridFiltersHidden() {
      return !this.gridOptions.floatingFilter
    },
    attachRowSelectedHandler() {
      return this.allRowsProgramaticallySelected
    },
  },
  watch: {
    selections(val) {
      this.allRowsProgramaticallySelected = false
      this.updateSelections(val)
    },
    rowData() {
      this.updateSelections(this.selections)
    },
    columnDefs() {
      this.updateSelections(this.selections)
    },
  },
  mounted() {
    this.gridApi = this.gridOptions.api
    this.updateSelections(this.selections)
  },
  methods: {
    rowId(row) {
      return row.data.id
    },
    handleChangeInSelection(selectedNodes) {
      if (this.disabled) return false
      if (!this.allRowsProgramaticallySelected) return false
      this.$emit('onSelectionChanged', selectedNodes)
    },
    handleRowSelected(selectedNode) {
      if (this.disabled) return false
      if (!this.allRowsProgramaticallySelected) return false
      this.$emit('onRowSelected', selectedNode)
    },
    updateSelections(selections) {
      this.allRowsProgramaticallySelected = false

      if (!selections.length && this.gridApi) {
        this.gridApi.deselectAll()
      } else {
        if (selections && selections.length && this.rowData.length) {
          const preSelected = _.isEqual(
            _.sortBy(_.map(this.gridApi.getSelectedRows(), 'id')),
            _.sortBy(selections),
          )

          if (!preSelected) {
            selections.forEach(id => {
              const node = this.gridApi.getRowNode(id)
              if (id !== null && node !== undefined)
                node.setSelected(true, false, true)
            })
          }
        }
      }

      // there needs to be a delay before this is flipped to true
      // for some reason when the node is selected, this is run immediately also
      setTimeout(() => {
        this.allRowsProgramaticallySelected = true
      }, 500)
    },
  },
}
</script>

<style lang="scss">
/* stylelint-disable no-descending-specificity */
.asc-ag-grid-filter--container-accent {
  .ag-theme-balham {
    .ag-root-wrapper {
      border: 0;
      overflow: visible;
    }

    .ag-header-cell {
      .ag-header-cell-text {
        text-transform: uppercase;
      }
    }

    .ag-menu-option {
      font-size: 14px;

      .ag-icon {
        display: none;
      }
    }

    .ag-floating-filter-button {
      display: none;
    }

    .ag-floating-filter-input {
      padding-top: 8px;
      color: $primary-black;
    }
  }
}

.asc-ag-grid-filter--container {
  .ag-theme-balham {
    .ag-root-wrapper-body.ag-layout-normal {
      height: auto;

      .ag-body-viewport {
        border: 1px solid $primary-white;
      }
    }

    .ag-header-container {
      background-color: $gray-300;
      border: 1px solid $primary-white;
    }

    .ag-header-viewport {
      background-color: $primary-white;
    }

    .ag-row {
      border: none;
      height: inherit;
    }

    .ag-row-odd {
      background-color: $gray-0;
    }

    .ag-row-even {
      background-color: $primary-white;
    }

    .ag-row--drill {
      &:hover {
        background-color: transparent;
      }
    }

    .ag-row--drill-accent {
      &:hover {
        background-color: $accent-0;
        cursor: pointer;
      }
    }

    .ag-row--drill-ripple {
      background-position: center;
      transition: background 1.8s;
    }

    .ag-row--drill-ripple:hover {
      // prettier-ignore
      background: $accent-0 radial-gradient(circle, transparent 1%, $accent-0 1%) center/15000%;
    }

    .ag-row--drill-ripple:active {
      background-color: $accent-100;
      background-size: 100%;
      transition: background 0s;
    }

    .ag-header-row:first-of-type {
      height: 42px !important;
    }

    .ag-header-cell {
      border-right: 1px solid $primary-white;
      color: $gray-600;
      height: inherit;
      background-color: $gray-300;

      .ag-header-cell-text {
        font-size: $font-size-sm;
      }

      .ag-header-icon {
        opacity: 1;

        .mdi-icon {
          position: absolute;
          top: 12px;
          right: 10px;

          .icon {
            font-size: 17px;
          }
        }

        .ag-icon {
          position: absolute;
          top: 12px;
          right: 10px;
          color: $gray-500;

          .icon {
            font-size: 17px;
          }
        }

        &.ag-sort-order {
          display: none;
        }
      }

      .ag-header-icon.ag-filter-icon {
        display: none;
      }
    }

    .ag-header-cell-sortable {
      &:hover {
        opacity: 0.8;
      }
    }

    .ag-cell-label-container {
      &.ag-header-cell-sorted-asc {
        &:hover {
          .ag-sort-ascending-icon {
            color: $gray-500;
          }
        }
      }

      &.ag-header-cell-sorted-desc {
        &:hover {
          .ag-sort-descending-icon {
            color: $gray-500;
          }
        }
      }
    }

    .ag-center-cols-container {
      border: none;
    }

    .ag-header-cell-label {
      color: $primary-black;
    }

    .ag-cell {
      color: $primary-black;
      font-size: $font-size-sm;
      line-height: inherit;
      padding: 12px;
      font-weight: 400;
    }

    .ag-cell--center {
      text-align: center !important;
    }

    .ag-cell--drill {
      &:hover {
        background-color: $accent-0;
        cursor: pointer;
      }
    }

    .ag-cell--drill-ripple {
      background-position: center;
      transition: background 0.8s;
    }

    .ag-cell--drill-ripple:hover {
      // prettier-ignore
      background: $accent-0 radial-gradient(circle, transparent 1%, $accent-0 1%) center/15000%;
    }

    .ag-cell--drill-ripple:active {
      background-color: $accent-100;
      background-size: 100%;
      transition: background 0s;
    }

    .ag-cell--flex {
      display: flex;
      justify-content: flex-start;
    }

    .no-border-top-left {
      border-left: 0;
      border-top: 0;
    }

    .ag-floating-filter-input {
      font-size: $font-size-sm;
    }

    .ag-filter-filter {
      font-size: $font-size-sm;
    }

    .ag-input-wrapper {
      margin-bottom: 2px;
    }

    .ag-status-bar {
      border: 0;
    }

    .ag-input-wrapper input:not([type]),
    .ag-input-wrapper input[type='text'],
    .ag-input-wrapper input[type='number'],
    .ag-input-wrapper input[type='tel'],
    .ag-input-wrapper input[type='date'],
    .ag-input-wrapper input[type='datetime-local'] {
      border: 0;
      border-bottom: 1px solid $gray-500;
      background-color: $gray-300;
    }

    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 8px;
      height: 8px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: rgba($primary-black, 0.35);
      box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }

    .ag-header-row:not(:first-child) {
      & .ag-header-cell {
        border-top: 0 !important;
      }

      & .ag-header-group-cell.ag-header-group-cell-with-group {
        border-top: 0 !important;
      }
    }
  }
}
/* stylelint-enable no-descending-specificity */
</style>
